/*
Confirmation modal for bulk vehicle unpublishing
Shows platform-specific unpublish options and selected vehicles
Author: Assistant
Date: 2025-01-21
*/
import React, { useState } from 'react'
import { Dialog, Flex, Text, Button, ScrollArea, Badge, Strong, Checkbox } from '@radix-ui/themes'

import Vehicle from '@/interfaces/Vehicle'
import { useFormatter } from '@/utils/formats'
import { activeChaConsumables } from '@/utils/chaProducts'

interface BulkUnpublishConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'>) => void
  selectedVehicles: Vehicle[]
  getBranchName: (branchId: string | null | undefined) => string
}

const BulkUnpublishConfirmationModal: React.FC<BulkUnpublishConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedVehicles,
  getBranchName,
}) => {
  const { formatCurrency } = useFormatter()
  const [selectedPlatforms, setSelectedPlatforms] = useState<Array<'mercadolibre' | 'chileautos' | 'yapo'>>([])

  // Count vehicles published on each platform
  const platformCounts = {
    mercadolibre: selectedVehicles.filter((v) => v.external_meli?.identifier).length,
    chileautos: selectedVehicles.filter((v) => v.external_chileautos?.identifier).length,
    yapo: selectedVehicles.filter((v) => v.external_yapo?.publishedAt).length,
  }

  // Despublicar de ChileAutos no devuelve un TopSpot/Showcase/TopNitro activo: se avisa cuáles se pierden.
  const withActiveHighlights = selectedVehicles.filter((v) => activeChaConsumables(v).length > 0)

  const handlePlatformToggle = (platform: 'mercadolibre' | 'chileautos' | 'yapo') => {
    setSelectedPlatforms((prev) => (prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]))
  }

  const handleConfirm = () => {
    if (selectedPlatforms.length > 0) {
      onConfirm(selectedPlatforms)
      onClose()
    }
  }

  const handleClose = () => {
    setSelectedPlatforms([])
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: 700, maxHeight: '85vh' }}>
        <Dialog.Title>Confirmar Despublicación Masiva</Dialog.Title>

        <Flex direction="column" gap="4" mt="4">
          {/* Platform Selection */}
          <Flex
            direction="column"
            gap="3"
            p="3"
            style={{ backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5' }}
          >
            <Text size="3" weight="medium" color="red">
              ⚠️ Seleccionar plataformas para despublicar
            </Text>

            <Flex direction="column" gap="2">
              <Flex align="center" gap="3">
                <Checkbox
                  checked={selectedPlatforms.includes('mercadolibre')}
                  onCheckedChange={() => handlePlatformToggle('mercadolibre')}
                  disabled={platformCounts.mercadolibre === 0}
                />
                <Badge color="yellow">MercadoLibre</Badge>
                <Text size="2" color="gray">
                  ({platformCounts.mercadolibre} vehículos publicados)
                </Text>
              </Flex>

              <Flex align="center" gap="3">
                <Checkbox
                  checked={selectedPlatforms.includes('chileautos')}
                  onCheckedChange={() => handlePlatformToggle('chileautos')}
                  disabled={platformCounts.chileautos === 0}
                />
                <Badge color="blue">ChileAutos</Badge>
                <Text size="2" color="gray">
                  ({platformCounts.chileautos} vehículos publicados)
                </Text>
              </Flex>

              <Flex align="center" gap="3">
                <Checkbox
                  checked={selectedPlatforms.includes('yapo')}
                  onCheckedChange={() => handlePlatformToggle('yapo')}
                  disabled={platformCounts.yapo === 0}
                />
                <Badge color="orange">Yapo</Badge>
                <Text size="2" color="gray">
                  ({platformCounts.yapo} vehículos publicados)
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {selectedPlatforms.includes('chileautos') && withActiveHighlights.length > 0 && (
            <Flex
              direction="column"
              gap="1"
              p="3"
              style={{ backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #f59e0b' }}
            >
              <Text size="2" weight="medium" color="orange">
                ⚠️ {withActiveHighlights.length} vehículo{withActiveHighlights.length > 1 ? 's tienen' : ' tiene'}{' '}
                destacados activos en ChileAutos
              </Text>
              <Text size="2">
                {withActiveHighlights
                  .map(
                    (v) =>
                      `${v.plate} (${activeChaConsumables(v)
                        .map((p) => p.name)
                        .join(', ')})`
                  )
                  .join(', ')}
                . Al despublicar se pierden los días restantes y el cupo no se reembolsa.
              </Text>
            </Flex>
          )}

          {/* Warning Message */}
          <Flex
            direction="column"
            gap="2"
            p="3"
            style={{ backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #f59e0b' }}
          >
            <Text size="3" weight="medium" color="orange">
              ⚠️ Confirmación requerida
            </Text>
            <Text size="2">Esta acción removerá los vehículos seleccionados de las plataformas elegidas.</Text>
            <Text size="2" color="gray">
              Los vehículos permanecerán en su inventario pero no estarán visibles en las plataformas externas.
            </Text>
          </Flex>

          {/* Vehicle List */}
          <ScrollArea style={{ height: 300 }}>
            <Flex direction="column" gap="2">
              {selectedVehicles.map((vehicle) => (
                <Flex
                  key={vehicle._id}
                  align="center"
                  justify="between"
                  p="2"
                  style={{ backgroundColor: '#f8fafc', borderRadius: '6px' }}
                >
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">
                      {vehicle.year} {vehicle.brand} {vehicle.model} {vehicle.version}
                    </Text>
                    <Flex align="center" gap="2">
                      <Text size="1" color="gray">
                        Patente: {vehicle.plate}
                      </Text>
                      <Text size="1" color="gray">
                        •
                      </Text>
                      <Text size="1" color="gray">
                        Sucursal: {getBranchName(vehicle.branch)}
                      </Text>
                    </Flex>

                    {/* Show current publication status */}
                    <Flex gap="1">
                      {vehicle.external_meli?.identifier && (
                        <Badge size="1" color="yellow">
                          ML
                        </Badge>
                      )}
                      {vehicle.external_chileautos?.identifier && (
                        <Badge size="1" color="blue">
                          ChA
                        </Badge>
                      )}
                      {vehicle.external_yapo?.publishedAt && (
                        <Badge size="1" color="orange">
                          Yapo
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                  <Text size="2" weight="bold">
                    {formatCurrency(vehicle.price)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </ScrollArea>

          {/* Summary */}
          <Flex
            direction="column"
            gap="2"
            p="3"
            style={{ backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          >
            <Flex align="center" justify="between">
              <Text size="3" weight="medium">
                Vehículos seleccionados:
              </Text>
              <Strong>{selectedVehicles.length}</Strong>
            </Flex>
            {selectedPlatforms.length > 0 && (
              <Flex align="center" justify="between">
                <Text size="3" weight="medium">
                  Plataformas seleccionadas:
                </Text>
                <Flex gap="1">
                  {selectedPlatforms.map((platform) => {
                    const colors: Record<string, 'yellow' | 'blue' | 'orange'> = {
                      mercadolibre: 'yellow',
                      chileautos: 'blue',
                      yapo: 'orange',
                    }
                    const labels = {
                      mercadolibre: 'ML',
                      chileautos: 'ChA',
                      yapo: 'Yapo',
                    }

                    return (
                      <Badge key={platform} size="1" color={colors[platform]}>
                        {labels[platform]}
                      </Badge>
                    )
                  })}
                </Flex>
              </Flex>
            )}
          </Flex>

          {/* Action Buttons */}
          <Flex justify="end" gap="3" pt="3" style={{ borderTop: '1px solid #e2e8f0' }}>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button color="red" onClick={handleConfirm} disabled={selectedPlatforms.length === 0}>
              Despublicar de {selectedPlatforms.length} plataforma{selectedPlatforms.length > 1 ? 's' : ''}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default BulkUnpublishConfirmationModal
